import { cn } from '@frontdoor/ui/lib/utils';
import { Check } from 'lucide-react';

import type { ProgressBarVariant, StatusStep } from '@/pages/track/status-config.tsx';

import type { StepState } from './ProgressStepIcon.tsx';
import { ProgressStepIcon } from './ProgressStepIcon.tsx';

type OrderProgressBarProps = {
  steps: StatusStep[];
  currentStepIndex: number;
  variant?: ProgressBarVariant;
};

const ICON_PADDING_PX = 32;
const TRACK_COLORS: Record<
  ProgressBarVariant,
  {
    completed: string;
    future: string;
    transitionMid: string;
    transitionEdge: string;
    firstEdge: string;
  }
> = {
  default: {
    completed: 'var(--color-hydrangea-700)',
    future: 'var(--color-sand-100)',
    transitionMid: 'var(--color-hydrangea-100)',
    transitionEdge: 'var(--color-hydrangea-700)',
    firstEdge: 'var(--color-hydrangea-700)',
  },
  action: {
    completed: 'var(--color-hydrangea-700)',
    future: 'var(--color-sand-100)',
    transitionMid: 'var(--color-rose-200)',
    transitionEdge: 'var(--color-rose-700)',
    firstEdge: 'var(--color-rose-700)',
  },
};

function stepPosition(index: number, stepCount: number): string {
  if (stepCount <= 1) return `${ICON_PADDING_PX}px`;
  if (index === 0) return `${ICON_PADDING_PX}px`;
  if (index === stepCount - 1) return `calc(100% - ${ICON_PADDING_PX}px)`;
  const totalPad = ICON_PADDING_PX * 2;
  return `calc(${ICON_PADDING_PX}px + ${index} * (100% - ${totalPad}px) / ${stepCount - 1})`;
}

function midpoint(aIndex: number, bIndex: number, stepCount: number): string {
  const totalPad = ICON_PADDING_PX * 2;
  const mid = (aIndex + bIndex) / 2;
  return `calc(${ICON_PADDING_PX}px + ${mid} * (100% - ${totalPad}px) / ${stepCount - 1})`;
}

function buildTrackGradient(
  stepCount: number,
  currentStepIndex: number,
  variant: ProgressBarVariant
): string {
  const { completed, future, transitionMid, transitionEdge, firstEdge } = TRACK_COLORS[variant];

  if (currentStepIndex >= stepCount) {
    return `linear-gradient(to right, ${completed} 0px, ${completed} 100%)`;
  }

  if (currentStepIndex <= 0) {
    const firstPos = stepPosition(0, stepCount);
    return [
      `linear-gradient(to right`,
      `${firstEdge} 0px`,
      `${future} ${firstPos}`,
      `${future} 100%)`,
    ].join(', ');
  }

  const prevPos = stepPosition(currentStepIndex - 1, stepCount);
  const midPos = midpoint(currentStepIndex - 1, currentStepIndex, stepCount);
  const currPos = stepPosition(currentStepIndex, stepCount);

  const stops = [`linear-gradient(to right`, `${completed} 0px`, `${completed} ${prevPos}`];

  if (variant === 'action') {
    stops.push(`${transitionEdge} ${prevPos}`);
  }

  stops.push(
    `${transitionMid} ${midPos}`,
    `${transitionEdge} ${currPos}`,
    `${future} ${currPos}`,
    `${future} 100%)`
  );

  return stops.join(', ');
}

function getStepState(
  index: number,
  currentStepIndex: number,
  variant: ProgressBarVariant
): StepState {
  if (index < currentStepIndex) return 'completed';
  if (index === currentStepIndex) {
    return variant === 'action' ? 'action' : 'current';
  }
  return 'future';
}

export function OrderProgressBar({
  steps,
  currentStepIndex,
  variant = 'default',
}: OrderProgressBarProps) {
  const trackGradient = buildTrackGradient(steps.length, currentStepIndex, variant);

  return (
    <div className="relative">
      <div
        className="absolute inset-x-0 top-3 h-1.5 rounded-full"
        style={{ background: trackGradient }}
      />

      <div className="relative flex justify-between px-10">
        {steps.map((step, i) => {
          const state = getStepState(i, currentStepIndex, variant);
          const isCompleted = state === 'completed';

          const label = isCompleted && step.completedLabel ? step.completedLabel : step.label;

          return (
            <div key={step.key} className="flex w-0 flex-col items-center gap-3">
              <ProgressStepIcon icon={isCompleted ? Check : step.icon} state={state} />
              <span
                className={cn(
                  'text-xs whitespace-nowrap',
                  isCompleted
                    ? 'font-medium text-charcoal-900'
                    : 'font-normal text-muted-foreground'
                )}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
